import { RowenaAvailableVariable } from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';

export type RowenaConditionalOperator =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'is_set'
  | 'is_empty'
  | 'is_not_empty';

export type RowenaConditionalValueKind = 'literal' | 'variable';

export interface RowenaConditionalAttrs {
  variable: string;
  option: string | null;
  operator: RowenaConditionalOperator;
  value: string;
  valueKind: RowenaConditionalValueKind;
}

export const DEFAULT_CONDITIONAL_ATTRS: RowenaConditionalAttrs = {
  variable: '',
  option: null,
  operator: 'eq',
  value: '',
  valueKind: 'literal',
};

export const OPERATOR_LABELS: Record<RowenaConditionalOperator, string> = {
  eq: 'equals',
  neq: 'not equals',
  gt: 'greater than',
  gte: 'greater or equal',
  lt: 'less than',
  lte: 'less or equal',
  is_set: 'is set',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
};

export const OPERATOR_SYMBOLS: Record<RowenaConditionalOperator, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  is_set: 'is set',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
};

const NUMERIC_OPERATORS = new Set<RowenaConditionalOperator>(['gt', 'gte', 'lt', 'lte']);
const VALUELESS_OPERATORS = new Set<RowenaConditionalOperator>(['is_set', 'is_empty', 'is_not_empty']);

export function operatorRequiresValue(op: RowenaConditionalOperator): boolean {
  return !VALUELESS_OPERATORS.has(op);
}

export function operatorIsNumeric(op: RowenaConditionalOperator): boolean {
  return NUMERIC_OPERATORS.has(op);
}

export function variablesCompatibleWithOperator(
  variables: RowenaAvailableVariable[],
  op: RowenaConditionalOperator
): RowenaAvailableVariable[] {
  if (operatorIsNumeric(op)) {
    return variables.filter((v) => v.valueType === 'number');
  }
  return variables;
}

export function conditionalVariableLookupKey(variable: string, option?: string | null): string {
  if (!variable) return '';
  return option ? `${variable}[${option}]` : variable;
}

export function formatConditionSummary(attrs: RowenaConditionalAttrs): string {
  const { variable, option, operator, value } = attrs;
  const lookupKey = conditionalVariableLookupKey(variable, option);
  const varRef = lookupKey ? `@${lookupKey}` : '(no variable)';
  if (!operatorRequiresValue(operator)) {
    return `${varRef} ${OPERATOR_SYMBOLS[operator]}`;
  }
  const valueRef = value || '(empty)';
  return `${varRef} ${OPERATOR_SYMBOLS[operator]} ${valueRef}`;
}
